import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Lock, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errorText } from "../lib/api";
import { formatPrice, waLink } from "../lib/site";

const loadCheckout = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const EMPTY = { name: "", phone: "", email: "", fulfilment: "delivery", address: "", city: "", pincode: "" };

const Input = ({ k, label, f, set, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-semibold text-navy">{label}</span>
    <input data-testid={`checkout-${k}-input`} value={f[k]} onChange={(e) => set(k, e.target.value)} className="field" {...props} />
  </label>
);

export default function CheckoutDialog({ product, onClose }) {
  const [qty, setQty] = useState(1);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (product) {
      setQty(1);
      setDone(null);
    }
  }, [product]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const total = (product?.price ?? 0) * qty;
  const delivery = f.fulfilment === "delivery";

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ok = await loadCheckout();
      if (!ok) throw new Error("Could not load the payment window. Check your connection and try again.");
      const { data } = await api.post("/orders/create", { product_id: product.id, quantity: qty, ...f });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "MHM SUPER ADBLUE",
        description: `${data.product_name} × ${qty}`,
        prefill: { name: f.name, contact: f.phone, email: f.email },
        theme: { color: "#FF6B00" },
        handler: async (res) => {
          try {
            const v = await api.post("/orders/verify", res);
            setDone(v.data);
          } catch (err) {
            toast.error(errorText(err));
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      toast.error(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkout-dialog" className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-lenis-prevent>
        {done ? (
          <div data-testid="checkout-success" className="py-6 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h3 className="mt-4 font-display text-4xl font-extrabold uppercase text-navy">Payment received</h3>
            <p className="mt-2 text-slate-600">{done.product_name} × {done.quantity} · {formatPrice(done.amount)}</p>
            <p className="mt-1 font-mono text-xs text-slate-500">Order ref: {done.order_ref}</p>
            <p className="mt-4 text-slate-600">
              {done.fulfilment === "pickup" ? "We will call you when your order is ready for pickup in Haldia." : "We will call you to confirm delivery."}
            </p>
            <a href={waLink(`Hello MHM SUPER ADBLUE, I just paid online. Order ref: ${done.order_ref}`)} target="_blank" rel="noreferrer" className="mt-6 inline-flex rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white">
              Message us on WhatsApp
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-bold uppercase text-navy">Buy online</DialogTitle>
              <DialogDescription>{product?.name}{product?.pack_size ? ` · ${product.pack_size}` : ""} · {formatPrice(product?.price)} {product?.unit}</DialogDescription>
            </DialogHeader>
            <form onSubmit={pay} className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 flex items-center justify-between rounded-2xl bg-[var(--paper)] p-4">
                <span className="text-sm font-semibold text-navy">Quantity</span>
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="checkout-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-9 w-9 place-items-center rounded-full border border-navy/15 bg-white"><Minus className="h-4 w-4" /></button>
                  <span data-testid="checkout-qty" className="w-8 text-center font-display text-2xl font-bold text-navy">{qty}</span>
                  <button type="button" data-testid="checkout-qty-plus" onClick={() => setQty((q) => Math.min(500, q + 1))} className="grid h-9 w-9 place-items-center rounded-full border border-navy/15 bg-white"><Plus className="h-4 w-4" /></button>
                </div>
              </div>
              <Input k="name" label="Full name *" f={f} set={set} required minLength={2} />
              <Input k="phone" label="Phone *" f={f} set={set} required type="tel" minLength={10} />
              <div className="sm:col-span-2"><Input k="email" label="Email (for receipt)" f={f} set={set} type="email" /></div>
              <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                {[["delivery", "Delivery"], ["pickup", "Pickup from Haldia"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`checkout-fulfilment-${v}`} onClick={() => set("fulfilment", v)} className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${f.fulfilment === v ? "border-brand bg-brand-soft text-brand" : "border-navy/15 text-navy"}`}>{l}</button>
                ))}
              </div>
              {delivery && (
                <>
                  <div className="sm:col-span-2"><Input k="address" label="Delivery address *" f={f} set={set} required minLength={5} /></div>
                  <Input k="city" label="City *" f={f} set={set} required />
                  <Input k="pincode" label="PIN code *" f={f} set={set} required pattern="[0-9]{6}" inputMode="numeric" />
                </>
              )}
              <div className="sm:col-span-2 mt-2 flex items-center justify-between border-t border-navy/10 pt-4">
                <span className="text-sm text-slate-600">Total</span>
                <span data-testid="checkout-total" className="font-display text-3xl font-extrabold text-navy">{formatPrice(total)}</span>
              </div>
              <button data-testid="checkout-pay-button" disabled={busy} className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand py-4 font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Pay {formatPrice(total)} securely
              </button>
              <p className="sm:col-span-2 text-center text-xs text-slate-500">Payments by Razorpay · UPI, cards, net banking</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
